import type { ApiError } from "@/types/api";

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? "/api";

const TOKEN_KEY = "sp_auth_token";

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

export class ApiRequestError extends Error {
  status: number;
  detail: string;

  constructor(status: number, detail: string) {
    super(detail);
    this.name = "ApiRequestError";
    this.status = status;
    this.detail = detail;
  }
}

type RequestOptions = Omit<RequestInit, "body"> & {
  body?: unknown;
  // Timeout in ms; default 15 seconds.
  timeoutMs?: number;
};

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { body, timeoutMs = 15_000, headers, ...rest } = options;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  const isFormData = typeof FormData !== "undefined" && body instanceof FormData;
  const token = getToken();
  const finalHeaders: Record<string, string> = {
    Accept: "application/json",
    // Let the browser set Content-Type (with boundary) for FormData.
    ...(body !== undefined && !isFormData ? { "Content-Type": "application/json" } : {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(headers as Record<string, string> | undefined),
  };

  let response: Response;
  try {
    response = await fetch(`${BASE_URL}${path}`, {
      ...rest,
      headers: finalHeaders,
      body:
        body === undefined
          ? undefined
          : isFormData
          ? (body as FormData)
          : JSON.stringify(body),
      signal: controller.signal,
    });
  } catch (err) {
    if ((err as Error).name === "AbortError") {
      throw new ApiRequestError(0, "Запрос превысил таймаут");
    }
    throw new ApiRequestError(0, "Нет связи с сервером");
  } finally {
    clearTimeout(timer);
  }

  if (response.status === 401) {
    clearToken();
    // Bubble up so AuthContext can re-initiate Telegram login.
    window.dispatchEvent(new CustomEvent("sp:unauthorized"));
    throw new ApiRequestError(401, "Сессия истекла. Войди заново.");
  }

  if (!response.ok) {
    let detail = response.statusText;
    try {
      const err = (await response.json()) as ApiError;
      detail = err.detail ?? detail;
    } catch {
      /* body may be empty or non-JSON */
    }
    throw new ApiRequestError(response.status, detail);
  }

  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

export const api = {
  get:  <T>(path: string, opts?: RequestOptions) => request<T>(path, { ...opts, method: "GET"  }),
  post: <T>(path: string, body?: unknown, opts?: RequestOptions) =>
    request<T>(path, { ...opts, method: "POST", body }),
  put:  <T>(path: string, body?: unknown, opts?: RequestOptions) =>
    request<T>(path, { ...opts, method: "PUT",  body }),
  patch:<T>(path: string, body?: unknown, opts?: RequestOptions) =>
    request<T>(path, { ...opts, method: "PATCH", body }),
  delete: <T>(path: string, opts?: RequestOptions) =>
    request<T>(path, { ...opts, method: "DELETE" }),
};
